import React, { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import Navigation from '@/components/Navigation'
import Footer from '@/components/Footer'
import { useProducts } from '@/hooks/useProducts'
import {Download, ShieldCheck, CheckCircle, FileText, ExternalLink} from 'lucide-react'

export default function ProductDetailPage() {
  const { slug } = useParams<{ slug: string }>()
  const { fetchProductBySlug } = useProducts()
  const [product, setProduct] = useState<any>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const loadProduct = async () => {
      if (slug) {
        setLoading(true)
        const foundProduct = await fetchProductBySlug(slug)
        setProduct(foundProduct)
        setLoading(false)
      }
    }
    loadProduct()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug])

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Navigation />
        <div className="flex items-center justify-center py-20">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
        <Footer />
      </div>
    )
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-white">
        <Navigation />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
          <h1 className="text-3xl font-bold text-slate-900 mb-4">Product Not Found</h1>
          <p className="text-slate-600 mb-8">The product you're looking for doesn't exist.</p>
          <Link to="/plugins" className="text-blue-600 hover:text-blue-700 font-semibold">
            ← Back to Products
          </Link>
        </div>
        <Footer />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-white">
      <Navigation />

      {/* Product Header */}
      <section className="bg-gradient-to-br from-blue-50 to-white py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <Link to="/plugins" className="text-blue-600 hover:text-blue-700 font-semibold mb-6 inline-block">
            ← Back to Products
          </Link>
          
          <div className="grid md:grid-cols-2 gap-12 items-start mt-8">
            {/* Product Image */}
            <div>
              {product.imageUrl && (
                <img
                  src={product.imageUrl}
                  alt={product.name}
                  className="w-full h-96 object-cover rounded-2xl shadow-lg"
                />
              )}
            </div>

            {/* Product Info */}
            <div>
              <div className="flex items-center space-x-3 mb-4">
                <ShieldCheck className="w-6 h-6 text-green-500" />
                <span className="text-sm font-semibold text-green-700">Official Licensed • 100% Secure Code</span>
              </div>

              <h1 className="text-4xl font-bold text-slate-900 mb-4">{product.name}</h1>
              <p className="text-xl text-slate-600 mb-6">{product.shortDescription}</p>

              <div className="bg-white border-2 border-blue-600 rounded-xl p-6 mb-6">
                <p className="text-4xl font-bold text-slate-900 mb-2">${product.price}</p>
                <p className="text-slate-600 mb-4">One-time payment • Lifetime license</p>
                
                <button className="w-full px-8 py-4 bg-blue-600 text-white font-bold rounded-lg hover:bg-blue-700 transition-colors text-lg shadow-lg hover:shadow-xl">
                  Purchase Now
                </button>
                
                <div className="mt-4 flex items-center justify-center space-x-2 text-sm text-slate-500">
                  <Download className="w-4 h-4" />
                  <span>Instant digital download after purchase</span>
                </div>
              </div>

              {/* Technical Specs */}
              {product.technicalSpecs && product.technicalSpecs.length > 0 && (
                <div className="bg-slate-50 rounded-lg p-4">
                  <h3 className="font-semibold text-slate-900 mb-2">Technical Requirements:</h3>
                  <ul className="space-y-1 text-sm text-slate-600">
                    {product.technicalSpecs.map((spec: string, index: number) => (
                      <li key={index}>• {spec}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-8">Key Features</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {product.features?.map((feature: string, index: number) => (
              <div key={index} className="flex items-start space-x-3 bg-blue-50 p-6 rounded-lg">
                <CheckCircle className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
                <div>
                  <p className="text-slate-900 font-semibold">{feature}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Description Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-6">Product Description</h2>
          <div 
            className="prose prose-slate max-w-none text-slate-700 leading-relaxed"
            dangerouslySetInnerHTML={{ __html: product.description }}
          />
        </div>
      </section>

      {/* Documentation & Support */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="border border-slate-200 rounded-xl p-8 hover:shadow-lg transition-all">
              <FileText className="w-12 h-12 text-blue-600 mb-4" />
              <h3 className="text-2xl font-bold text-slate-900 mb-3">Documentation</h3>
              <p className="text-slate-600 mb-4">Complete installation guides, API references, and code examples.</p>
              <a 
                href={product.documentationUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center space-x-2 text-blue-600 hover:text-blue-700 font-semibold"
              >
                <span>View Documentation</span>
                <ExternalLink className="w-4 h-4" />
              </a>
            </div>

            <div className="border border-slate-200 rounded-xl p-8 hover:shadow-lg transition-all">
              <ShieldCheck className="w-12 h-12 text-green-600 mb-4" />
              <h3 className="text-2xl font-bold text-slate-900 mb-3">Premium Support</h3>
              <p className="text-slate-600 mb-4">Get help from our expert team. Priority support included with purchase.</p>
              <Link 
                to="/support"
                className="inline-flex items-center space-x-2 text-blue-600 hover:text-blue-700 font-semibold"
              >
                <span>Contact Support</span>
                <ExternalLink className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  )
}
